import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FiBell, FiCheck, FiTrash2, FiX } from 'react-icons/fi';
import API from '../api/axios';
import './NotificationBell.css';

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 8 });
  const ref = useRef(null);
  const btnRef = useRef(null);
  const dropRef = useRef(null);

  const fetchNotifications = useCallback(() => {
    API.get('/notifications').then(r => {
      setNotifications(r.data);
      setUnread(r.data.filter(n => !n.isRead).length);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchNotifications();
    // poll every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // close on outside click — check both bell button and portal dropdown
  useEffect(() => {
    const handler = (e) => {
      const inBtn = ref.current?.contains(e.target);
      const inDrop = dropRef.current?.contains(e.target);
      if (!inBtn && !inDrop) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (id) => {
    await API.put(`/notifications/${id}/read`).catch(() => {});
    setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await API.put('/notifications/mark-all-read').catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnread(0);
  };

  const deleteOne = async (id, e) => {
    e.stopPropagation();
    await API.delete(`/notifications/${id}`).catch(() => {});
    const n = notifications.find(n => n._id === id);
    setNotifications(prev => prev.filter(n => n._id !== id));
    if (n && !n.isRead) setUnread(prev => Math.max(0, prev - 1));
  };

  const clearRead = async () => {
    await API.delete('/notifications/clear/read').catch(() => {});
    setNotifications(prev => prev.filter(n => !n.isRead));
  };

  const timeAgo = (date) => {
    const diff = (Date.now() - new Date(date)) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const TYPE_ICON = { attendance: '🕐', general: '📢' };

  return (
    <div className="notif-wrap" ref={ref}>
      <button className="notif-bell-btn" ref={btnRef} onClick={() => {
        if (!open && btnRef.current) {
          const r = btnRef.current.getBoundingClientRect();
          // position dropdown so its right edge aligns with the button's right edge
          setDropPos({ top: r.bottom + 10, left: Math.max(8, r.right - 360) });
        }
        setOpen(o => !o);
      }}>
        <FiBell />
        {unread > 0 && <span className="notif-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && createPortal(
        <div className="notif-dropdown" ref={dropRef} style={{ top: dropPos.top, left: dropPos.left }}>
          <div className="notif-header">
            <h3>Notifications {unread > 0 && <span className="notif-unread-label">{unread} new</span>}</h3>
            <div className="notif-header-actions">
              {unread > 0 && (
                <button className="notif-action-btn" onClick={markAllRead} title="Mark all read">
                  <FiCheck size={13} /> All read
                </button>
              )}
              <button className="notif-action-btn" onClick={clearRead} title="Clear read">
                <FiTrash2 size={13} /> Clear
              </button>
              <button className="notif-close-btn" onClick={() => setOpen(false)}><FiX /></button>
            </div>
          </div>

          <div className="notif-list">
            {notifications.length === 0 ? (
              <div className="notif-empty">
                <FiBell size={28} />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n._id}
                  className={`notif-item ${!n.isRead ? 'unread' : ''}`}
                  onClick={() => !n.isRead && markRead(n._id)}
                >
                  <div className="notif-icon">{TYPE_ICON[n.type] || '📢'}</div>
                  <div className="notif-body">
                    <p className="notif-message">{n.message}</p>
                    <div className="notif-meta">
                      {n.sender?.name && <span className="notif-sender">{n.sender.name}</span>}
                      <span className="notif-time">{timeAgo(n.createdAt)}</span>
                    </div>
                  </div>
                  {!n.isRead && <span className="notif-dot" />}
                  <button className="notif-del-btn" onClick={(e) => deleteOne(n._id, e)}><FiX size={12} /></button>
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
